import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { SAGE, CREAM, WARM } from '../../theme';
import { mockAuthHeaders } from '../../utils/mockAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * "Coming Soon" placeholder with optional upvote button.
 *
 * Props:
 *   featureKey  - unique key like "job_family.overview.comp_bands"
 *   title       - short label, e.g. "Compensation Bands"
 *   description - optional detail text
 *   icon        - optional MUI icon element
 *   compact     - smaller inline variant (default false)
 */
const ComingSoon = ({ featureKey, title, description, icon, compact = false }) => {
  const [voted, setVoted] = useState(false);
  const [count, setCount] = useState(null);

  // Check if user already voted (local storage for instant feedback)
  useEffect(() => {
    const votes = JSON.parse(localStorage.getItem('onward_feature_votes') || '{}');
    if (votes[featureKey]) setVoted(true);
  }, [featureKey]);

  const handleUpvote = async (e) => {
    e.stopPropagation();
    if (voted) return;

    // Optimistic update
    setVoted(true);
    setCount((c) => (c || 0) + 1);
    const votes = JSON.parse(localStorage.getItem('onward_feature_votes') || '{}');
    votes[featureKey] = true;
    localStorage.setItem('onward_feature_votes', JSON.stringify(votes));

    try {
      await fetch(`${API_URL}/api/feature-requests/upvote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ feature_key: featureKey }),
      });
    } catch (err) {
      console.error('Failed to upvote:', err);
    }
  };

  if (compact) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: CREAM[200],
          border: `1px dashed ${SAGE[300]}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: SAGE[500],
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
          }}
        >
          Coming soon
        </Typography>
        <Tooltip title={voted ? 'Requested!' : 'I want this'} placement="top">
          <IconButton size="small" onClick={handleUpvote} sx={{ p: 0.3 }}>
            {voted ? (
              <ThumbUpIcon sx={{ fontSize: 14, color: SAGE[500] }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: 14, color: SAGE[400] }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: compact ? 3 : 5,
        px: 3,
        bgcolor: CREAM[100],
        borderRadius: 2.5,
        border: `2px dashed ${SAGE[300]}`,
      }}
    >
      {icon && (
        <Box sx={{ color: SAGE[300], mb: 1.5 }}>
          {React.cloneElement(icon, { sx: { fontSize: 36 } })}
        </Box>
      )}
      <Typography
        sx={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 600,
          fontSize: '1rem',
          color: SAGE[700],
          mb: 0.5,
        }}
      >
        {title || 'Coming Soon'}
      </Typography>
      {description && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: SAGE[400],
            fontFamily: "'Outfit', sans-serif",
            mb: 2,
            maxWidth: 400,
            mx: 'auto',
          }}
        >
          {description}
        </Typography>
      )}
      <Box
        onClick={handleUpvote}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          cursor: voted ? 'default' : 'pointer',
          bgcolor: voted ? SAGE[100] : 'transparent',
          border: `1.5px solid ${voted ? SAGE[400] : SAGE[300]}`,
          transition: 'all 0.15s',
          '&:hover': voted ? {} : { borderColor: SAGE[500], bgcolor: SAGE[50] },
        }}
      >
        {voted ? (
          <ThumbUpIcon sx={{ fontSize: 16, color: SAGE[600] }} />
        ) : (
          <ThumbUpOutlinedIcon sx={{ fontSize: 16, color: SAGE[400] }} />
        )}
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            color: voted ? SAGE[700] : SAGE[500],
          }}
        >
          {voted ? 'Requested!' : 'I want this'}
        </Typography>
        {count != null && count > 0 && (
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              color: SAGE[500],
              bgcolor: SAGE[100],
              px: 1,
              py: 0.2,
              borderRadius: 1,
            }}
          >
            {count}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ComingSoon;
